import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import ImageModal from './ImageModal';
import PlanetUserManagement from './PlanetUserManagement';

const Container = styled.div`
  min-height: 100vh;
  background: #f5f7fa;
`;

const Header = styled.div`
  background: white;
  padding: 20px 30px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const Title = styled.h1`
  color: #333;
  margin: 0;
  font-size: 24px;
  font-weight: 700;
`;

const UserInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 15px;
`;

const AdminBadge = styled.span`
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  padding: 6px 12px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 600;
`;

const LogoutButton = styled.button`
  background: #ff4757;
  color: white;
  border: none;
  padding: 8px 16px;
  border-radius: 6px;
  cursor: pointer;
  font-size: 14px;
  transition: all 0.3s ease;
  
  &:hover {
    background: #ff3742;
    transform: translateY(-1px);
  }
`;

const Content = styled.div`
  padding: 30px;
  max-width: 1200px;
  margin: 0 auto;
`;

const TabContainer = styled.div`
  display: flex;
  gap: 10px;
  margin-bottom: 30px;
  border-bottom: 2px solid #e1e5e9;
`;

const Tab = styled.button`
  background: none;
  border: none;
  padding: 12px 24px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  color: ${props => props.active ? '#667eea' : '#666'};
  border-bottom: 3px solid ${props => props.active ? '#667eea' : 'transparent'};
  transition: all 0.3s ease;
  
  &:hover {
    color: #667eea;
  }
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 20px;
  margin-bottom: 30px;
`;

const StatCard = styled.div`
  background: white;
  padding: 25px;
  border-radius: 12px;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.08);
  border-left: 4px solid ${props => props.color || '#667eea'};
`;

const StatTitle = styled.h3`
  color: #666;
  margin: 0 0 10px 0;
  font-size: 14px;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const StatValue = styled.div`
  color: #333;
  font-size: 32px;
  font-weight: 700;
  margin-bottom: 5px;
`;

const StatChange = styled.div`
  color: ${props => props.positive ? '#2ed573' : '#ff4757'};
  font-size: 12px;
  font-weight: 500;
`;

const TableContainer = styled.div`
  background: white;
  border-radius: 12px;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.08);
  overflow: hidden;
`;

const TableHeader = styled.div`
  background: #f8f9fa;
  padding: 20px 25px;
  border-bottom: 1px solid #e1e5e9;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const TableTitle = styled.h2`
  color: #333;
  margin: 0;
  font-size: 18px;
  font-weight: 600;
`;

const SearchInput = styled.input`
  padding: 8px 12px;
  border: 1px solid #ddd;
  border-radius: 6px;
  font-size: 14px;
  width: 200px;
  
  &:focus {
    outline: none;
    border-color: #667eea;
  }
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
`;

const TableHead = styled.thead`
  background: #f8f9fa;
`;

const TableRow = styled.tr`
  &:nth-child(even) {
    background: #f8f9fa;
  }
  
  &:hover {
    background: #e3f2fd;
  }
`;

const TableCell = styled.td`
  padding: 15px 20px;
  border-bottom: 1px solid #e1e5e9;
  font-size: 14px;
  color: #333;
`;

const TableHeaderCell = styled.th`
  padding: 15px 20px;
  text-align: left;
  font-weight: 600;
  color: #555;
  font-size: 14px;
  border-bottom: 2px solid #e1e5e9;
`;

const StatusBadge = styled.span`
  padding: 4px 8px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 500;
  background: ${props => {
    switch (props.status) {
      case 'completed': return '#d4edda';
      case 'pending': return '#fff3cd';
      case 'processing': return '#cce5ff';
      case 'failed': return '#f8d7da';
      default: return '#e2e3e5';
    }
  }};
  color: ${props => {
    switch (props.status) {
      case 'completed': return '#155724';
      case 'pending': return '#856404';
      case 'processing': return '#004085';
      case 'failed': return '#721c24';
      default: return '#383d41';
    }
  }};
`;

const ActionButton = styled.button`
  background: ${props => props.danger ? '#ff4757' : '#667eea'};
  color: white;
  border: none;
  padding: 6px 12px;
  border-radius: 4px;
  cursor: pointer;
  font-size: 12px;
  margin-right: 5px;
  transition: all 0.3s ease;
  
  &:hover {
    opacity: 0.8;
    transform: translateY(-1px);
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    transform: none;
  }
`;

const LoadingSpinner = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 40px;
  font-size: 16px;
  color: #666;
`;

const ErrorMessage = styled.div`
  background: #fee;
  color: #c33;
  padding: 15px;
  border-radius: 8px;
  border: 1px solid #fcc;
  margin: 20px 0;
  text-align: center;
`;

const Modal = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
`;

const ModalContent = styled.div`
  background: white;
  padding: 30px;
  border-radius: 12px;
  width: 90%;
  max-width: 500px;
  max-height: 80vh;
  overflow-y: auto;
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
`;

const ModalTitle = styled.h2`
  margin: 0;
  color: #333;
  font-size: 20px;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 24px;
  cursor: pointer;
  color: #666;
  padding: 0;
  
  &:hover {
    color: #333;
  }
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 15px;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 5px;
`;

const Label = styled.label`
  font-weight: 600;
  color: #333;
  font-size: 14px;
`;

const Input = styled.input`
  padding: 10px 12px;
  border: 1px solid #ddd;
  border-radius: 6px;
  font-size: 14px;
  
  &:focus {
    outline: none;
    border-color: #667eea;
  }
`;

const Checkbox = styled.input`
  margin-right: 8px;
`;

const CheckboxLabel = styled.label`
  display: flex;
  align-items: center;
  font-size: 14px;
  color: #333;
  cursor: pointer;
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 10px;
  justify-content: flex-end;
  margin-top: 20px;
`;

const Button = styled.button`
  padding: 10px 20px;
  border: none;
  border-radius: 6px;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.3s ease;
  
  ${props => props.primary ? `
    background: #667eea;
    color: white;
    
    &:hover {
      background: #5a6fd8;
    }
  ` : `
    background: #f8f9fa;
    color: #333;
    border: 1px solid #ddd;
    
    &:hover {
      background: #e9ecef;
    }
  `}
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const AddButton = styled.button`
  background: #2ed573;
  color: white;
  border: none;
  padding: 10px 20px;
  border-radius: 6px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 600;
  transition: all 0.3s ease;
  
  &:hover {
    background: #26d065;
    transform: translateY(-1px);
  }
`;

const ThumbnailContainer = styled.div`
  display: flex;
  gap: 5px;
  flex-wrap: wrap;
  max-width: 200px;
`;

const Thumbnail = styled.img`
  width: 40px;
  height: 40px;
  object-fit: cover;
  border-radius: 4px;
  cursor: pointer;
  border: 2px solid transparent;
  transition: all 0.3s ease;
  
  &:hover {
    border-color: #667eea;
    transform: scale(1.1);
  }
`;

const NoImageText = styled.span`
  color: #999;
  font-style: italic;
  font-size: 12px;
`;

// 分页组件样式
const PaginationContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px 25px;
  background: #f8f9fa;
  border-top: 1px solid #e1e5e9;
`;

const PaginationInfo = styled.div`
  color: #666;
  font-size: 14px;
`;

const PaginationControls = styled.div`
  display: flex;
  gap: 10px;
  align-items: center;
`;

const PageButton = styled.button`
  background: ${props => props.active ? '#667eea' : 'white'};
  color: ${props => props.active ? 'white' : '#333'};
  border: 1px solid ${props => props.active ? '#667eea' : '#ddd'};
  padding: 8px 12px;
  border-radius: 6px;
  cursor: pointer;
  font-size: 14px;
  transition: all 0.3s ease;
  
  &:hover:not(:disabled) {
    background: ${props => props.active ? '#5a6fd8' : '#f8f9fa'};
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const PageInput = styled.input`
  width: 60px;
  padding: 6px 8px;
  border: 1px solid #ddd;
  border-radius: 4px;
  text-align: center;
  font-size: 14px;
  
  &:focus {
    outline: none;
    border-color: #667eea;
  }
`;

function AdminDashboard({ admin, onLogout }) {
  const [activeTab, setActiveTab] = useState('dashboard');
  const [statistics, setStatistics] = useState(null);
  const [users, setUsers] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  
  // 分页相关状态
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalTasks, setTotalTasks] = useState(0);
  const [pageSize] = useState(10); // 每页显示数量
  
  // 用户分页相关状态
  const [userCurrentPage, setUserCurrentPage] = useState(1);
  const [userTotalPages, setUserTotalPages] = useState(1);
  const [totalUsers, setTotalUsers] = useState(0);
  const [userPageSize] = useState(10); // 每页显示用户数量
  
  // 用户表单相关状态
  const [showUserModal, setShowUserModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [userForm, setUserForm] = useState({
    username: '',
    password: '',
    planet_code: '',
    planet_nickname: '',
    is_verified: false
  });
  const [formLoading, setFormLoading] = useState(false);
  
  // 图片模态框状态
  const [imageModal, setImageModal] = useState({
    isOpen: false,
    images: [],
    currentIndex: 0,
    taskId: null
  });

  const token = localStorage.getItem('adminToken');

  // 格式化时间为北京时间
  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    // 转换为北京时间 (UTC+8)
    const beijingTime = new Date(date.getTime() + (8 * 60 * 60 * 1000));
    return beijingTime.toLocaleString('zh-CN', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      timeZone: 'Asia/Shanghai'
    });
  };

  const fetchStatistics = async () => {
    try {
      const response = await fetch('/api/admin/statistics', {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      const data = await response.json();
      if (data.success) {
        setStatistics(data.data);
      } else {
        setError(data.message);
      }
    } catch (error) {
      console.error('获取统计数据失败:', error);
      setError('获取统计数据失败');
    }
  };

  const fetchUsers = async (page = userCurrentPage) => {
    setLoading(true);
    try {
      const response = await fetch(`/api/admin/users?search=${searchTerm}&page=${page}&limit=${userPageSize}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      const data = await response.json();
      if (data.success) {
        setUsers(data.data.users);
        // 解析分页数据
        setUserTotalPages(data.data.pagination.total_pages || 1);
        setTotalUsers(data.data.pagination.total_items || 0);
        setUserCurrentPage(page);
      } else {
        setError(data.message);
      }
    } catch (error) {
      console.error('获取用户列表失败:', error);
      setError('获取用户列表失败');
    } finally {
      setLoading(false);
    }
  };

  const fetchTasks = async (page = currentPage) => {
    setLoading(true);
    try {
      const response = await fetch(`/api/admin/tasks?page=${page}&limit=${pageSize}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      const data = await response.json();
      if (data.success) {
        setTasks(data.data.tasks);
        // 修正分页数据的解析
        setTotalPages(data.data.pagination.total_pages || 1);
        setTotalTasks(data.data.pagination.total_items || 0);
        setCurrentPage(page);
      } else {
        setError(data.message);
      }
    } catch (error) {
      console.error('获取作品列表失败:', error);
      setError('获取作品列表失败');
    } finally {
      setLoading(false);
    }
  };

  // 刷新单个任务状态
  const refreshSingleTaskStatus = async (taskId) => {
    try {
      const response = await fetch(`/api/admin/refresh-task?taskId=${taskId}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      const data = await response.json();
      if (data.success) {
        // 刷新成功后重新获取任务列表
        await fetchTasks();
        setError(''); // 清除之前的错误
        // 显示成功消息
        alert(`任务 ${taskId} 状态更新成功\n状态: ${data.data?.status || 'unknown'}\n进度: ${data.data?.progress || 0}%`);
      } else {
        setError(data.message);
        // 格式化错误信息显示
        const errorMessage = `任务 ${taskId} 状态更新失败:\n\n${data.message}`;
        alert(errorMessage);
      }
    } catch (error) {
      console.error('刷新任务状态失败:', error);
      setError('刷新任务状态失败');
      const errorMessage = `任务 ${taskId} 状态更新失败:\n\nAPI请求失败，HTTP状态码: ${error.message}`;
      alert(errorMessage);
    }
  };

  const batchRefreshTaskStatus = async () => {
    // eslint-disable-next-line no-restricted-globals
    if (!confirm('确定要批量刷新所有需要重新查询的任务吗？\n包括：已完成但无图片的任务、失败但有外部任务ID的任务。\n这可能需要一些时间。')) {
      return;
    }
    
    setLoading(true);
    try {
      const response = await fetch('/api/admin/batch-refresh-tasks', {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      const data = await response.json();
      if (data.success) {
        // 刷新成功后重新获取任务列表
        await fetchTasks();
        setError(''); // 清除之前的错误
        
        // 显示详细的批量刷新结果
        const resultMessage = `批量刷新完成！\n\n` +
          `总任务数: ${data.data.total_tasks}\n` +
          `成功数: ${data.data.success_count}\n` +
          `失败数: ${data.data.failed_count}\n\n` +
          (data.data.success_tasks.length > 0 ? 
            `成功的任务ID: ${data.data.success_tasks.map(t => t.id).join(', ')}\n` : '') +
          (data.data.failed_tasks.length > 0 ? 
            `失败的任务ID: ${data.data.failed_tasks.map(t => t.id).join(', ')}` : '');
        
        alert(resultMessage);
      } else {
        setError(data.message);
        alert(`批量刷新失败: ${data.message}`);
      }
    } catch (error) {
      console.error('批量刷新任务状态失败:', error);
      setError('批量刷新任务状态失败');
      alert(`批量刷新失败: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleUserStatusToggle = async (userId, currentStatus) => {
    try {
      const response = await fetch(`/api/admin/users/${userId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ is_verified: !currentStatus })
      });
      const data = await response.json();
      if (data.success) {
        fetchUsers(); // 重新获取用户列表
      } else {
        setError(data.message);
      }
    } catch (error) {
      console.error('更新用户状态失败:', error);
      setError('更新用户状态失败');
    }
  };

  const handleDeleteUser = async (userId) => {
    if (!window.confirm('确定要删除这个用户吗？此操作不可恢复。')) {
      return;
    }
    
    try {
      const response = await fetch(`/api/admin/users/${userId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      const data = await response.json();
      if (data.success) {
        fetchUsers(); // 重新获取用户列表
      } else {
        setError(data.message);
      }
    } catch (error) {
      console.error('删除用户失败:', error);
      setError('删除用户失败');
    }
  };

  const handleDeleteTask = async (taskId) => {
    if (!window.confirm('确定要删除这个作品吗？此操作不可恢复。')) {
      return;
    }
    
    try {
      const response = await fetch(`/api/admin/tasks/${taskId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      const data = await response.json();
      if (data.success) {
        fetchTasks(); // 重新获取作品列表
      } else {
        setError(data.message);
      }
    } catch (error) {
      console.error('删除作品失败:', error);
      setError('删除作品失败');
    }
  };
  
  // 用户表单相关函数
  const openUserModal = (user = null) => {
    if (user) {
      setEditingUser(user);
      setUserForm({
        username: user.username,
        password: '',
        planet_code: user.planet_code || '',
        planet_nickname: user.planet_nickname || '',
        is_verified: user.is_verified
      });
    } else {
      setEditingUser(null);
      setUserForm({
        username: '',
        password: '',
        planet_code: '',
        planet_nickname: '',
        is_verified: false
      });
    }
    setShowUserModal(true);
  };
  
  const closeUserModal = () => {
    setShowUserModal(false);
    setEditingUser(null);
    setUserForm({
      username: '',
      password: '',
      planet_code: '',
      planet_nickname: '',
      is_verified: false
    });
  };
  
  const handleUserFormChange = (e) => {
    const { name, value, type, checked } = e.target;
    setUserForm(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };
  
  const handleUserFormSubmit = async (e) => {
    e.preventDefault();
    setFormLoading(true);
    
    try {
      const url = editingUser 
        ? `/api/admin/users/${editingUser.id}`
        : '/api/admin/users';
      
      const method = editingUser ? 'PUT' : 'POST';
      
      // 如果是编辑且密码为空，不发送密码字段
      const formData = { ...userForm };
      if (editingUser && !formData.password) {
        delete formData.password;
      }
      
      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(formData)
      });
      
      const data = await response.json();
      if (data.success) {
        closeUserModal();
        fetchUsers(); // 重新获取用户列表
        setError('');
      } else {
        setError(data.message);
      }
    } catch (error) {
      console.error('保存用户失败:', error);
      setError('保存用户失败');
    } finally {
      setFormLoading(false);
    }
  };
  
  // 图片模态框处理函数
  const openImageModal = (task, imageIndex = 0) => {
    // 使用从后端返回的实际图片URL
    const images = task.result_images || [];
    
    if (images.length === 0) {
      setError('该作品暂无图片');
      return;
    }
    
    setImageModal({
      isOpen: true,
      images,
      currentIndex: imageIndex,
      taskId: task.id
    });
  };
  
  const closeImageModal = () => {
    setImageModal({
      isOpen: false,
      images: [],
      currentIndex: 0,
      taskId: null
    });
  };
  
  const handleImagePrevious = () => {
    setImageModal(prev => ({
      ...prev,
      currentIndex: Math.max(0, prev.currentIndex - 1)
    }));
  };
  
  const handleImageNext = () => {
    setImageModal(prev => ({
      ...prev,
      currentIndex: Math.min(prev.images.length - 1, prev.currentIndex + 1)
    }));
  };

  useEffect(() => {
    fetchStatistics();
  }, []);

  useEffect(() => {
    if (activeTab === 'users') {
      fetchUsers();
    } else if (activeTab === 'tasks') {
      fetchTasks();
    }
  }, [activeTab, searchTerm]);

  const renderDashboard = () => (
    <div>
      {statistics && (
        <StatsGrid>
          <StatCard color="#667eea">
            <StatTitle>总用户数</StatTitle>
            <StatValue>{statistics.users.total_users}</StatValue>
            <StatChange positive>已验证: {statistics.users.verified_users}</StatChange>
          </StatCard>
          
          <StatCard color="#2ed573">
            <StatTitle>总作品数</StatTitle>
            <StatValue>{statistics.tasks.total_tasks}</StatValue>
            <StatChange positive>已完成: {statistics.tasks.completed_tasks}</StatChange>
          </StatCard>
          
          <StatCard color="#ffa502">
            <StatTitle>今日新用户</StatTitle>
            <StatValue>{statistics.users.today_new_users}</StatValue>
          </StatCard>
          
          <StatCard color="#ff4757">
            <StatTitle>今日作品</StatTitle>
            <StatValue>{statistics.tasks.today_tasks}</StatValue>
          </StatCard>
        </StatsGrid>
      )}
      
    </div>
  );

  const renderUsers = () => (
    <TableContainer>
      <TableHeader>
        <TableTitle>用户管理</TableTitle>
        <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
          <SearchInput
            type="text"
            placeholder="搜索用户..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          <AddButton onClick={() => openUserModal()}>
            新增用户
          </AddButton>
        </div>
      </TableHeader>
      
      {loading ? (
        <LoadingSpinner>加载中...</LoadingSpinner>
      ) : (
        <>
          <Table>
            <TableHead>
              <TableRow>
                <TableHeaderCell>ID</TableHeaderCell>
                <TableHeaderCell>用户名</TableHeaderCell>
                <TableHeaderCell>星球昵称</TableHeaderCell>
                <TableHeaderCell>星球编号</TableHeaderCell>
                <TableHeaderCell>状态</TableHeaderCell>
                <TableHeaderCell>作品数</TableHeaderCell>
                <TableHeaderCell>注册时间</TableHeaderCell>
                <TableHeaderCell>操作</TableHeaderCell>
              </TableRow>
            </TableHead>
            <tbody>
              {users.map(user => (
                <TableRow key={user.id}>
                  <TableCell>{user.id}</TableCell>
                  <TableCell>{user.username}</TableCell>
                  <TableCell>{user.planet_nickname}</TableCell>
                  <TableCell>{user.planet_code}</TableCell>
                  <TableCell>
                    <StatusBadge status={user.is_verified ? 'completed' : 'pending'}>
                      {user.is_verified ? '已验证' : '未验证'}
                    </StatusBadge>
                  </TableCell>
                  <TableCell>{user.task_count} / {user.completed_count}</TableCell>
                  <TableCell>{formatDate(user.created_at)}</TableCell>
                  <TableCell>
                    <ActionButton
                      onClick={() => openUserModal(user)}
                    >
                      编辑
                    </ActionButton>
                    <ActionButton
                      onClick={() => handleUserStatusToggle(user.id, user.is_verified)}
                    >
                      {user.is_verified ? '禁用' : '启用'}
                    </ActionButton>
                    <ActionButton
                      danger
                      onClick={() => handleDeleteUser(user.id)}
                    >
                      删除
                    </ActionButton>
                  </TableCell>
                </TableRow>
              ))}
            </tbody>
          </Table>
          {renderUserPagination()}
        </>
      )}
    </TableContainer>
  );

  // 作品分页处理函数
  const handlePageChange = (page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
      fetchTasks(page);
    }
  };

  const handlePageInputChange = (e) => {
    const value = e.target.value;
    if (value === '' || (parseInt(value) >= 1 && parseInt(value) <= totalPages)) {
      if (value !== '' && parseInt(value) !== currentPage) {
        handlePageChange(parseInt(value));
      }
    }
  };

  // 用户分页处理函数
  const handleUserPageChange = (page) => {
    if (page >= 1 && page <= userTotalPages) {
      setUserCurrentPage(page);
      fetchUsers(page);
    }
  };

  const handleUserPageInputChange = (e) => {
    const value = e.target.value;
    if (value === '' || (parseInt(value) >= 1 && parseInt(value) <= userTotalPages)) {
      if (value !== '' && parseInt(value) !== userCurrentPage) {
        handleUserPageChange(parseInt(value));
      }
    }
  };

  // 作品分页组件
  const renderPagination = () => {
    if (totalPages <= 1) return null;

    const pages = [];
    const maxVisiblePages = 5;
    let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
    let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);

    if (endPage - startPage + 1 < maxVisiblePages) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }

    for (let i = startPage; i <= endPage; i++) {
      pages.push(i);
    }

    return (
      <PaginationContainer>
        <PaginationInfo>
          显示第 {(currentPage - 1) * pageSize + 1} - {Math.min(currentPage * pageSize, totalTasks)} 条，共 {totalTasks} 条记录
        </PaginationInfo>
        <PaginationControls>
          <PageButton
            disabled={currentPage === 1}
            onClick={() => handlePageChange(1)}
          >
            首页
          </PageButton>
          <PageButton
            disabled={currentPage === 1}
            onClick={() => handlePageChange(currentPage - 1)}
          >
            上一页
          </PageButton>
          
          {startPage > 1 && (
            <>
              <PageButton onClick={() => handlePageChange(1)}>1</PageButton>
              {startPage > 2 && <span>...</span>}
            </>
          )}
          
          {pages.map(page => (
            <PageButton
              key={page}
              active={page === currentPage}
              onClick={() => handlePageChange(page)}
            >
              {page}
            </PageButton>
          ))}
          
          {endPage < totalPages && (
            <>
              {endPage < totalPages - 1 && <span>...</span>}
              <PageButton onClick={() => handlePageChange(totalPages)}>{totalPages}</PageButton>
            </>
          )}
          
          <PageButton
            disabled={currentPage === totalPages}
            onClick={() => handlePageChange(currentPage + 1)}
          >
            下一页
          </PageButton>
          <PageButton
            disabled={currentPage === totalPages}
            onClick={() => handlePageChange(totalPages)}
          >
            末页
          </PageButton>
          
          <span style={{ margin: '0 10px', color: '#666' }}>跳转到</span>
          <PageInput
            type="number"
            min="1"
            max={totalPages}
            value={currentPage}
            onChange={handlePageInputChange}
          />
          <span style={{ color: '#666' }}>页</span>
        </PaginationControls>
      </PaginationContainer>
    );
  };

  // 用户分页组件
  const renderUserPagination = () => {
    if (userTotalPages <= 1) return null;

    const pages = [];
    const maxVisiblePages = 5;
    let startPage = Math.max(1, userCurrentPage - Math.floor(maxVisiblePages / 2));
    let endPage = Math.min(userTotalPages, startPage + maxVisiblePages - 1);

    if (endPage - startPage + 1 < maxVisiblePages) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }

    for (let i = startPage; i <= endPage; i++) {
      pages.push(i);
    }

    return (
      <PaginationContainer>
        <PaginationInfo>
          显示第 {(userCurrentPage - 1) * userPageSize + 1} - {Math.min(userCurrentPage * userPageSize, totalUsers)} 条，共 {totalUsers} 条记录
        </PaginationInfo>
        <PaginationControls>
          <PageButton
            disabled={userCurrentPage === 1}
            onClick={() => handleUserPageChange(1)}
          >
            首页
          </PageButton>
          <PageButton
            disabled={userCurrentPage === 1}
            onClick={() => handleUserPageChange(userCurrentPage - 1)}
          >
            上一页
          </PageButton>
          
          {startPage > 1 && (
            <>
              <PageButton onClick={() => handleUserPageChange(1)}>1</PageButton>
              {startPage > 2 && <span>...</span>}
            </>
          )}
          
          {pages.map(page => (
            <PageButton
              key={page}
              active={page === userCurrentPage}
              onClick={() => handleUserPageChange(page)}
            >
              {page}
            </PageButton>
          ))}
          
          {endPage < userTotalPages && (
            <>
              {endPage < userTotalPages - 1 && <span>...</span>}
              <PageButton onClick={() => handleUserPageChange(userTotalPages)}>{userTotalPages}</PageButton>
            </>
          )}
          
          <PageButton
            disabled={userCurrentPage === userTotalPages}
            onClick={() => handleUserPageChange(userCurrentPage + 1)}
          >
            下一页
          </PageButton>
          <PageButton
            disabled={userCurrentPage === userTotalPages}
            onClick={() => handleUserPageChange(userTotalPages)}
          >
            末页
          </PageButton>
          
          <span style={{ margin: '0 10px', color: '#666' }}>跳转到</span>
          <PageInput
            type="number"
            min="1"
            max={userTotalPages}
            value={userCurrentPage}
            onChange={handleUserPageInputChange}
          />
          <span style={{ color: '#666' }}>页</span>
        </PaginationControls>
      </PaginationContainer>
    );
  };

  const renderTasks = () => (
    <TableContainer>
      <TableHeader>
          <TableTitle>作品管理</TableTitle>
          <ActionButton
            onClick={batchRefreshTaskStatus}
            disabled={loading}
            style={{ marginLeft: 'auto', backgroundColor: '#1890ff', color: 'white' }}
          >
            {loading ? '批量刷新中...' : '批量刷新状态'}
          </ActionButton>
        </TableHeader>
      
      {loading ? (
        <LoadingSpinner>加载中...</LoadingSpinner>
      ) : (
        <>
          <Table>
            <TableHead>
              <TableRow>
                <TableHeaderCell>ID</TableHeaderCell>
                <TableHeaderCell>用户</TableHeaderCell>
                <TableHeaderCell>提示词</TableHeaderCell>
                <TableHeaderCell>尺寸</TableHeaderCell>
                <TableHeaderCell>外部任务ID</TableHeaderCell>
                <TableHeaderCell>状态</TableHeaderCell>
                <TableHeaderCell>图片预览</TableHeaderCell>
                <TableHeaderCell>创建时间</TableHeaderCell>
                <TableHeaderCell>操作</TableHeaderCell>
              </TableRow>
            </TableHead>
            <tbody>
              {tasks.map(task => (
                <TableRow key={task.id}>
                  <TableCell>{task.id}</TableCell>
                  <TableCell>{task.username}</TableCell>
                  <TableCell title={task.prompt}>
                    {task.prompt.length > 50 ? task.prompt.substring(0, 50) + '...' : task.prompt}
                  </TableCell>
                  <TableCell>{task.aspect_ratio}</TableCell>
                  <TableCell>
                    {task.external_task_id ? (
                      <code style={{fontSize: '11px', background: '#f5f5f5', padding: '2px 4px', borderRadius: '3px', wordBreak: 'break-all'}}>
                        {task.external_task_id}
                      </code>
                    ) : (
                      <span style={{color: '#999', fontSize: '12px'}}>无</span>
                    )}
                  </TableCell>
                  <TableCell>
                    <StatusBadge status={task.status}>
                      {task.status === 'completed' ? '已完成' :
                       task.status === 'pending' ? '等待中' :
                       task.status === 'processing' ? '处理中' : 
                       task.status === 'failed' && task.external_task_id ? '失败(可重试)' : '失败'}
                    </StatusBadge>
                  </TableCell>
                  <TableCell>
                     {task.status === 'completed' && task.result_images && task.result_images.length > 0 ? (
                       <ThumbnailContainer>
                         {task.result_images.map((imageUrl, index) => (
                           <Thumbnail
                              key={index}
                              src={imageUrl}
                              alt={`作品 ${task.id} 图片 ${index + 1}`}
                              onClick={() => openImageModal(task, index)}
                              onError={(e) => {
                                e.target.style.display = 'none';
                              }}
                            />
                         ))}
                       </ThumbnailContainer>
                     ) : (
                       <NoImageText>暂无图片</NoImageText>
                     )}
                   </TableCell>
                  <TableCell>{formatDate(task.created_at)}</TableCell>
                  <TableCell>
                    <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                      {(task.external_task_id && (task.status === 'pending' || task.status === 'processing' || task.status === 'failed')) && (
                        <ActionButton
                          onClick={() => refreshSingleTaskStatus(task.id)}
                          style={{ 
                            fontSize: '12px', 
                            padding: '4px 8px',
                            backgroundColor: task.status === 'failed' ? '#ff7875' : '#1890ff',
                            borderColor: task.status === 'failed' ? '#ff7875' : '#1890ff'
                          }}
                        >
                          {task.status === 'failed' ? '重试查询' : '刷新状态'}
                        </ActionButton>
                      )}
                      <ActionButton
                        danger
                        onClick={() => handleDeleteTask(task.id)}
                        style={{ fontSize: '12px', padding: '4px 8px' }}
                      >
                        删除
                      </ActionButton>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </tbody>
          </Table>
          {renderPagination()}
        </>
      )}
    </TableContainer>
  );

  return (
    <Container>
      <Header>
        <Title>管理员后台</Title>
        <UserInfo>
          <span>欢迎，{admin.username}</span>
          <AdminBadge>管理员</AdminBadge>
          <LogoutButton onClick={onLogout}>退出登录</LogoutButton>
        </UserInfo>
      </Header>
      
      <Content>
        <TabContainer>
          <Tab 
            active={activeTab === 'dashboard'} 
            onClick={() => setActiveTab('dashboard')}
          >
            仪表板
          </Tab>
          <Tab 
            active={activeTab === 'users'} 
            onClick={() => setActiveTab('users')}
          >
            用户管理
          </Tab>
          <Tab 
            active={activeTab === 'tasks'} 
            onClick={() => setActiveTab('tasks')}
          >
            作品管理
          </Tab>
          <Tab 
            active={activeTab === 'planet-users'} 
            onClick={() => setActiveTab('planet-users')}
          >
            星球用户管理
          </Tab>
        </TabContainer>
        
        {error && <ErrorMessage>{error}</ErrorMessage>}
        
        {activeTab === 'dashboard' && renderDashboard()}
        {activeTab === 'users' && renderUsers()}
        {activeTab === 'tasks' && renderTasks()}
        {activeTab === 'planet-users' && <PlanetUserManagement />}
      </Content>
      
      {/* 用户表单模态框 */}
      {showUserModal && (
        <Modal onClick={(e) => e.target === e.currentTarget && closeUserModal()}>
          <ModalContent>
            <ModalHeader>
              <ModalTitle>{editingUser ? '编辑用户' : '新增用户'}</ModalTitle>
              <CloseButton onClick={closeUserModal}>×</CloseButton>
            </ModalHeader>
            
            <Form onSubmit={handleUserFormSubmit}>
              <FormGroup>
                <Label>用户名 *</Label>
                <Input
                  type="text"
                  name="username"
                  value={userForm.username}
                  onChange={handleUserFormChange}
                  required
                  placeholder="请输入用户名"
                />
              </FormGroup>
              
              <FormGroup>
                <Label>密码 {editingUser ? '(留空则不修改)' : '*'}</Label>
                <Input
                  type="password"
                  name="password"
                  value={userForm.password}
                  onChange={handleUserFormChange}
                  required={!editingUser}
                  placeholder={editingUser ? "留空则不修改密码" : "请输入密码"}
                />
              </FormGroup>
              
              <FormGroup>
                <Label>星球编号</Label>
                <Input
                  type="text"
                  name="planet_code"
                  value={userForm.planet_code}
                  onChange={handleUserFormChange}
                  placeholder="请输入星球编号"
                />
              </FormGroup>
              
              <FormGroup>
                <Label>星球昵称</Label>
                <Input
                  type="text"
                  name="planet_nickname"
                  value={userForm.planet_nickname}
                  onChange={handleUserFormChange}
                  placeholder="请输入星球昵称"
                />
              </FormGroup>
              
              <FormGroup>
                <CheckboxLabel>
                  <Checkbox
                    type="checkbox"
                    name="is_verified"
                    checked={userForm.is_verified}
                    onChange={handleUserFormChange}
                  />
                  已验证用户
                </CheckboxLabel>
              </FormGroup>
              
              <ButtonGroup>
                <Button type="button" onClick={closeUserModal}>
                  取消
                </Button>
                <Button type="submit" primary disabled={formLoading}>
                  {formLoading ? '保存中...' : (editingUser ? '更新' : '创建')}
                </Button>
              </ButtonGroup>
            </Form>
          </ModalContent>
        </Modal>
      )}
      
      {/* 图片模态框 */}
      {imageModal.isOpen && (
        <ImageModal
          isOpen={imageModal.isOpen}
          imageUrl={imageModal.images[imageModal.currentIndex]}
          images={imageModal.images}
          currentIndex={imageModal.currentIndex}
          onClose={closeImageModal}
          onPrevious={handleImagePrevious}
          onNext={handleImageNext}
          title={`作品 ${imageModal.taskId} - 图片 ${imageModal.currentIndex + 1}`}
        />
      )}
    </Container>
  );
}

export default AdminDashboard;